import { Checkbox, RadioButton } from '@ics-portal/guardian';
import { type ReactNode } from 'react';
import { type ZoneModels } from '@models/Zone';
import { formatDate } from '@utils/dateFormatUtils';
import { isDefined } from '@utils/isDefined';
import { LinkToRequirements } from './LinkToRequirements';

interface ProfilesSectionProps {
    standard: ZoneModels.Standard;
    selectedProfileIds: string[];
    onProfileChange: (profileId: string, checked: boolean) => void;
    onStandardClear: (standardId: string) => void;
    allowedProfileIds?: string[];
    zoneId?: string;
    projectId?: string;
    readonly?: boolean;
    invalid?: boolean;
    singleSelect?: boolean;
}

export function ProfilesSection({
    standard,
    selectedProfileIds,
    onProfileChange,
    onStandardClear,
    allowedProfileIds,
    zoneId,
    projectId,
    readonly = false,
    invalid = false,
    singleSelect = false,
}: ProfilesSectionProps): ReactNode {
    const allProfiles = standard.standardProfiles?.filter(isDefined) ?? [];
    const profiles = allowedProfileIds
        ? allProfiles.filter((p) => allowedProfileIds.includes(p.id))
        : allProfiles;

    const profilesIds = profiles.map((p) => p.id);
    const isProfileSelectedForStandard =
        selectedProfileIds.filter((profileId) =>
            profilesIds.includes(profileId),
        ).length > 0;

    const getCheckboxLabel = (profile: ZoneModels.StandardProfile): string => {
        return `${profile.name} - ${formatDate(profile.standard?.releasedAt ?? '')}`;
    };

    return (
        <div className="flex flex-column gap-2">
            {profiles.map((profile) => {
                const requirementCount = standard.requirementsCount || 0;
                const isSelected = selectedProfileIds.includes(profile.id);

                return (
                    <div
                        className="flex align-items-center gap-2"
                        key={profile.id}
                    >
                        {singleSelect ? (
                            <RadioButton
                                name={`standard-${standard.id}`}
                                inputId={profile.id}
                                value={profile.id}
                                checked={isSelected}
                                disabled={readonly}
                                invalid={invalid}
                                onCheckedChange={(checked) => {
                                    onProfileChange(profile.id, checked);
                                }}
                                toggleable
                            />
                        ) : (
                            <Checkbox
                                label={getCheckboxLabel(profile)}
                                checked={isSelected}
                                disabled={readonly}
                                invalid={invalid}
                                onChange={(e) => {
                                    onProfileChange(
                                        profile.id,
                                        e.checked ?? false,
                                    );
                                }}
                            />
                        )}
                        {singleSelect ? (
                            <button
                                type="button"
                                className="cursor-pointer bg-transparent border-none p-0 text-left"
                                disabled={readonly}
                                onClick={() => {
                                    onProfileChange(profile.id, !isSelected);
                                }}
                            >
                                {getCheckboxLabel(profile)}
                            </button>
                        ) : null}
                        {requirementCount > 0 && (
                            <LinkToRequirements
                                profile={profile}
                                standard={standard}
                                requirementCount={requirementCount}
                                zoneId={zoneId}
                                projectId={projectId}
                            />
                        )}
                    </div>
                );
            })}
            {singleSelect ? (
                <div className="flex align-items-center gap-2">
                    <RadioButton
                        name={`${standard.id}-no-standard`}
                        inputId={`${standard.id}-no-standard`}
                        value={undefined}
                        checked={!isProfileSelectedForStandard}
                        disabled={readonly}
                        invalid={invalid}
                        onCheckedChange={(checked) => {
                            if (checked) {
                                onStandardClear(standard.id);
                            }
                        }}
                    />
                    <button
                        type="button"
                        className="cursor-pointer bg-transparent border-none p-0 text-left"
                        disabled={readonly}
                        onClick={() => {
                            onStandardClear(standard.id);
                        }}
                    >
                        None
                    </button>
                </div>
            ) : null}
        </div>
    );
}
